import { Globe } from 'lucide-react';
// NOTE: Brand icons (Facebook, LinkedIn, Instagram, etc.) are kept from react-icons
// because lucide-react doesn't provide brand/logo icons. These are necessary for
// proper UX as users expect to see recognizable social network brand icons.
import { FaFacebookF, FaLinkedinIn, FaReddit, FaTelegram, FaWhatsapp } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { SiGoogle, SiInstagram, SiYoutube } from 'react-icons/si';

import { useTheme } from 'hooks/useTheme';

interface IconProps {
  color?: string;
  size?: number;
  className?: string;
  useThemeColor?: boolean;
  themeColorKey?: keyof Pick<
    import('hooks/useTheme').ThemeColors,
    'primary' | 'success' | 'warning' | 'info' | 'error'
  >;
}

// Utility function that receives theme as parameter
export const getSocialNetworkIconWithTheme = (
  networkType: string,
  theme: any,
  iconProps: IconProps = {}
): JSX.Element | null => {
  const {
    color,
    size = 16,
    className,
    useThemeColor = false,
    themeColorKey = 'primary',
  } = iconProps;

  let finalColor = color;
  if (useThemeColor && theme) {
    finalColor = theme[themeColorKey];
  } else if (!color) {
    finalColor = theme.primary;
  }

  const iconStyle = {
    color: finalColor,
    fontSize: size,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  switch (networkType) {
    case 'FACEBOOK':
      return <FaFacebookF style={iconStyle} className={className} />;
    case 'INSTAGRAM':
      return <SiInstagram style={iconStyle} className={className} />;
    case 'LINKEDIN':
      return <FaLinkedinIn style={iconStyle} className={className} />;
    case 'YOUTUBE':
      return <SiYoutube style={iconStyle} className={className} />;
    case 'X':
      return <FaXTwitter style={iconStyle} className={className} />;
    case 'REDDIT':
      return <FaReddit style={iconStyle} className={className} />;
    case 'GOOGLE':
      return <SiGoogle style={iconStyle} className={className} />;
    case 'TELEGRAM':
      return <FaTelegram style={iconStyle} className={className} />;
    case 'WHATSAPP':
      return <FaWhatsapp style={iconStyle} className={className} />;
    case 'WEBSITE':
      // Using inline style for dynamic size/color from theme context
      return (
        <Globe style={{ width: size, height: size, color: finalColor }} className={className} />
      );
    default:
      return null;
  }
};

// React component that can use hooks
export const SocialNetworkIcon: React.FC<{
  networkType: string;
  iconProps?: IconProps;
}> = ({ networkType, iconProps = {} }) => {
  const { theme } = useTheme();
  return getSocialNetworkIconWithTheme(networkType, theme, iconProps);
};

// Compatibility function that uses the component internally
export const getSocialNetworkIcon = (
  networkType: string,
  iconProps: IconProps = {}
): JSX.Element | null => {
  return <SocialNetworkIcon networkType={networkType} iconProps={iconProps} />;
};
